export enum OnboardingStep {
  Income,
  Essentials,
  Needs,
  Goals,
  Wants,
}

// This indicates what type of group it is.
// MultiInputs is only used for Income currently
// Checkboxes are used for the rest
export enum OnboardingGroupType {
  MultiInputs,
  Checkboxes,
}

export const OnboardingStepDefinition = {
  [OnboardingStep.Income]: {
    heading: 'Income',
    icon: '/images/steps/income.png',
    description:
      'First, let’s talk income! Please add each income stream you’d like to track as part of your budget:',
  },
  [OnboardingStep.Essentials]: {
    heading: 'Essentials',
    icon: '/images/steps/home.png',
    description: `Now, let’s figure out the essential expenses you want to track in your budget! These are the
      essential items that almost everyone will have, and often regularly scheduled amounts, like utilities and
      transportation. We’ll ask you about your variable needs (like groceries) later!`,
  },
  [OnboardingStep.Needs]: {
    heading: 'Needs',
    icon: '/images/steps/utility.png',
    description: `You’re almost halfway done! Let’s get your needs organized. These expenses are commonly
      considered necessities, but everyone’s needs may vary depending on your life stage or situation. This is
      where you start to have some wiggle room as you build out your budget with Good Steward.
      Don’t worry if you forget anything—you can always add more categories later!`,
  },
  [OnboardingStep.Goals]: {
    heading: 'Purpose and Goals',
    icon: '/images/steps/entertainment.png',
    description: `This is a good time to mention that our name, Good Steward, comes from our passion for managing
      money well to reach important financial goals and support the people and causes we care about.
      Whether it’s recovery from debt, building up your savings, or generous giving, we know that having a
      plan is a huge key to success. Start setting your goals for your money by checking off the categories
      that matter to you!`,
  },
  [OnboardingStep.Wants]: {
    heading: 'Wants',
    icon: '/images/steps/other.png',
    description: `Hooray! You’ve made it to the last step! Get ready to start planning and put your vision for your
      money into action. In this step, you can select categories for the things you want (but maybe don’t need). Once
      your budget is ready, you can explore Good Steward’s reporting tools to evaluate how you spend on your wants
      so you can discover opportunities to spend, save, and contribute to your passions!`,
  },
};

// This is used to indicate what group type this is. When creating the groups and categories,
// we'll use this to determine is_income and is_donation flags.
export enum GroupType {
  Income = 'income',
  Expense = 'expense',
  Donation = 'donation',
}

export interface GuidedOnboardingPayload {
  isEntirelySkipped: boolean;
  skipToAccounts?: boolean;
  groups?: {
    name: string; // For matching only, user cannot edit
    categories: string[];
  }[];
}

export interface AIOnboardingPayload {
  categoryGroups: {
    name: string;
    categories: {
      id: string;
      name: string;
      amount: number;
      frequency: string;
    }[];
    type: GroupType;
  }[];
  transactions: {
    date: string;
    amount: number;
    description: string;
    categoryId: string;
    accountId: string;
  }[];
  accounts: {
    id: string;
    name: string;
    balances: {
      current: number;
      available: number;
      at: string | null;
    };
    isBalanceOnlyAccount: boolean;
    isOnBudget: boolean;
  }[];
}

export interface IGroupDefinition {
  name: string;
  groupType: GroupType;
  onboarding: {
    step: OnboardingStep;
    groupType: OnboardingGroupType;
  };
  categories: ICategoryDefinition[];
}

export interface ICategoryDefinition {
  name: string;
}

// Those definitions will be used for guided onboarding, mint import and demo budget
export const CategoryDefinitions: IGroupDefinition[] = [
  {
    name: 'Income',
    groupType: GroupType.Income,
    onboarding: {
      step: OnboardingStep.Income,
      groupType: OnboardingGroupType.MultiInputs,
    },
    categories: [
      { name: 'Employment income' },
      { name: 'Business income' },
      { name: 'Rental income' },
      { name: 'Investment income' },
      { name: 'Government payments' },
      { name: 'Benefits' },
      { name: 'Bank deposits' },
      { name: 'Incoming Transfer' },
      { name: 'Tax refund' },
      { name: 'Other income' },
    ],
  },
  {
    name: 'Home',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Essentials,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Mortgage' },
      { name: 'Property tax' },
      { name: 'Rent' },
      { name: 'Home maintenance' },
      { name: 'Other home expenses' },
    ],
  },
  {
    name: 'Utilities',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Essentials,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Heating' },
      { name: 'Electricity' },
      { name: 'Water' },
      { name: 'Internet' },
      { name: 'Phone' },
      { name: 'Other utilities' },
    ],
  },
  {
    name: 'Transportation',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Essentials,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Auto loan/lease' },
      { name: 'Gas / Fuel' },
      { name: 'Public transport' },
      { name: 'Parking' },
      { name: 'Auto maintenance' },
      { name: 'Other transportation' },
    ],
  },
  {
    name: 'Insurance',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Essentials,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Home insurance' },
      { name: 'Renter insurance' },
      { name: 'Auto insurance' },
      { name: 'Life insurance' },
      { name: 'Critical illness insurance' },
      { name: 'Disability insurance' },
      { name: 'Other insurance' },
    ],
  },
  {
    name: 'Living expenses',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Needs,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Supplies' },
      { name: 'Groceries' },
      { name: 'Clothing' },
      { name: 'Self-care' },
      { name: 'Laundry / Dry cleaning' },
      { name: 'Other living expenses' },
    ],
  },
  {
    name: 'Family',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Needs,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Child care' },
      { name: 'Children\'s supplies' },
      { name: 'School' },
      { name: 'Tuition' },
      { name: 'Tutoring' },
      { name: 'Celebrations' },
      { name: 'Other family expenses' },
    ],
  },
  {
    name: 'Health',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Needs,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Dental' },
      { name: 'Vision' },
      { name: 'Medication' },
      { name: 'Out of pocket medical expenses' },
      { name: 'Other health expenses' },
    ],
  },
  {
    name: 'Debt',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Goals,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Credit Card' },
      { name: 'Student Loans' },
      { name: 'Personal Loans' },
      { name: 'Medical Debt' },
      { name: 'Other debt payments' },
    ],
  },
  {
    name: 'Savings/investments',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Goals,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Emergency fund' },
      { name: 'Savings' },
      { name: 'Investment' },
    ],
  },
  {
    name: 'Generosity',
    groupType: GroupType.Donation,
    onboarding: {
      step: OnboardingStep.Goals,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [{ name: 'Donations' }],
  },
  {
    name: 'Entertainment',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Wants,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Eating out' },
      { name: 'TV' },
      { name: 'Music' },
      { name: 'Books' },
      { name: 'Movies' },
      { name: 'Technology' },
      { name: 'Hobbies' },
      { name: 'Travel / Vacations' },
      { name: 'Other entertainment expenses' },
    ],
  },
  {
    name: 'Misc',
    groupType: GroupType.Expense,
    onboarding: {
      step: OnboardingStep.Wants,
      groupType: OnboardingGroupType.Checkboxes,
    },
    categories: [
      { name: 'Online shopping' },
      { name: 'Pets' },
      { name: 'Extra Curriculars' },
      { name: 'Events' },
      { name: 'Memberships/Registrations' },
      { name: 'Subscriptions' },
      { name: 'Gifts' },
      { name: 'Professional Services' },
      { name: 'Reimbursable Expenses' },
      { name: 'Banking / Financial services fees' },
      { name: 'Tax owing' },
      { name: 'Outgoing Transfer' },
      { name: 'Other expenses' },
    ],
  },
];
